/**
 * A player's streaks: the sheet the profile's Best streak tile opens, on anyone's profile — Blockari's `shell/StreakSheet`
 * (c258375), on its `PfSheet` markup (`w21-pf-*`, lifted). The server's `/api/users/:username/streaks` is public: settled
 * results only, no ids. Two lists, because the tile's "5X" is the in-game streak multiplier while the streak chests reward
 * a run of challenge wins: CARD STREAKS (Blockari's "Combos": one count per round, at its best) and WIN STREAKS
 * (consecutive decided wins). One row per exact length, longest first: how many times and when the latest was. A bottom
 * sheet on a phone, a centred card on a desktop.
 */

import type { Result, TopStreaks } from '@/net/api';
import { el } from '@/ui/hud';
import { segmented } from '@/ui/segmented';
import { STREAK_TABS, cardTitle, currentLine, emptyCopy, firstTab, timesLabel, whenLine, winTitle, type StreakTab } from '@/ui/streak-copy';

const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>';

let open: { layer: HTMLElement; off: () => void } | null = null;

export function closeStreakSheet(): void {
  open?.off();
  open?.layer.remove();
  open = null;
}

/** How many times, big on the right: "12 times", "1 time". */
function count(n: number): HTMLElement {
  const c = el('span', 'w21-pf-count');
  c.append(el('b', undefined, n.toLocaleString()), el('small', undefined, timesLabel(n)));
  return c;
}

function empty(tab: StreakTab): HTMLElement {
  const copy = emptyCopy(tab);
  const box = el('div', 'w21-empty');
  box.append(el('h3', undefined, copy.title), el('p', undefined, copy.text));
  return box;
}

/** The list under the tabs (a test seam): the rows of one tab, or its empty state. */
export function streakList(streaks: TopStreaks, tab: StreakTab): HTMLElement[] {
  if (tab === 'cards') {
    if (streaks.combos.length === 0) return [empty('cards')];
    const ol = el('ol', 'w21-pf-scores w21-pf-streaks');
    ol.setAttribute('aria-label', 'Card streaks by length');
    streaks.combos.forEach((c, i) => {
      const li = el('li', i === 0 ? 'w21-top' : undefined);
      li.append(el('b', 'w21-pf-score', cardTitle(c.length)), el('span', 'w21-pf-when', whenLine(c)), count(c.count));
      ol.append(li);
    });
    return [ol];
  }
  const out: HTMLElement[] = [];
  // "Current: <b>2</b> wins in a row": the line's own words, the number picked out.
  const now = /^(Current: )(\d+)( .*)$/.exec(currentLine(streaks.currentWinRun) ?? '');
  if (now) {
    const p = el('p', 'w21-pf-current w21-t-caption');
    p.append(document.createTextNode(now[1] ?? ''), el('b', undefined, now[2]), document.createTextNode(now[3] ?? ''));
    out.push(p);
  }
  if (streaks.winRuns.length === 0) return [...out, empty('wins')];
  const ol = el('ol', 'w21-pf-scores w21-pf-streaks');
  ol.setAttribute('aria-label', 'Win streaks by length');
  streaks.winRuns.forEach((r, i) => {
    const li = el('li', i === 0 ? 'w21-top' : undefined);
    const title = el('b', 'w21-pf-score', winTitle(r.length));
    if (r.ongoing) title.append(el('em', 'w21-pf-live', 'Ongoing'));
    li.append(title, el('span', 'w21-pf-when', whenLine(r)), count(r.count));
    ol.append(li);
  });
  return [...out, ol];
}

function skeleton(): HTMLElement {
  const box = el('div', 'w21-pf-skels');
  box.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 4; i++) {
    const s = el('div', 'w21-skeleton w21-card');
    s.style.animationDelay = `${i * 80}ms`;
    box.append(s);
  }
  return box;
}

/** Opens the sheet for `username` at once, with placeholders, and fills it when `load` answers. */
export function openStreakSheet(username: string, load: (username: string) => Promise<Result<TopStreaks>>): void {
  closeStreakSheet();
  const layer = el('div', 'w21-pf-layer');
  const sheet = el('div', 'w21-pf-sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'w21-pf-streaks-title');
  const head = el('div', 'w21-pf-sheet-head');
  const title = el('h2', undefined, 'Streaks');
  title.id = 'w21-pf-streaks-title';
  const close = el('button', 'w21-btn w21-ghost w21-sm w21-icon') as HTMLButtonElement;
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = ICON_X;
  close.addEventListener('click', closeStreakSheet);
  head.append(title, close);
  const body = el('div', 'w21-pf-body');
  body.append(skeleton());
  const grab = el('i', 'w21-pf-grab');
  grab.setAttribute('aria-hidden', 'true');
  sheet.append(grab, head, body);
  layer.append(sheet);
  layer.addEventListener('click', (e) => {
    if (e.target === layer) closeStreakSheet();
  });
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeStreakSheet();
  };
  document.addEventListener('keydown', onKey);
  document.body.append(layer);
  open = { layer, off: () => document.removeEventListener('keydown', onKey) };
  close.focus();

  void load(username).then((r) => {
    if (open?.layer !== layer) return;
    if (!r.ok) {
      const err = el('p', 'w21-t-caption w21-pf-error', 'Could not load the streaks.');
      err.setAttribute('role', 'alert');
      body.replaceChildren(err);
      return;
    }
    const data = r.value;
    const list = el('div', 'w21-pf-list-host');
    const tabs = segmented<StreakTab>('Streak kind', STREAK_TABS, (t) => show(t), { tone: 'warm' });
    const show = (t: StreakTab): void => {
      tabs.set(t);
      list.replaceChildren(...streakList(data, t));
    };
    body.replaceChildren(tabs.root, list);
    show(firstTab(data));
  });
}
